import { Link } from 'react-router-dom';
import Logo from '../components/ui/Logo';
import LincolnAvatar from '../components/ui/LincolnAvatar';
import Waveform from '../components/ui/Waveform';
import Stat from '../components/ui/Stat';
import ImageSlot from '../components/ui/ImageSlot';
import { COURSES } from '../data/courses';
import { CASE_STUDIES } from '../data/caseStudies';

/**
 * Mobile homepage (390px). Standalone — uses its own compact nav and footer
 * rather than the desktop chrome. Visit at /m to preview.
 */
export default function HomeMobile() {
  return (
    <div className="mx-auto" style={{ maxWidth: 390, fontSize: 14, background: 'var(--bg)' }}>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 bg-white flex items-center"
        style={{ borderBottom: '1px solid var(--line)', padding: '10px 16px', gap: 8 }}>
        <Logo size={28} />
        <button type="button" className="ml-auto inline-flex items-center justify-center rounded-xl"
          style={{ width: 40, height: 40, background: 'var(--bg-soft)' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
        </button>
        <button type="button" className="inline-flex items-center justify-center rounded-xl"
          style={{ width: 40, height: 40, background: 'var(--bg-soft)' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.4"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(160deg, #0b1f38 0%, #102a4a 100%)', padding: '32px 20px 40px' }}>
        <div className="absolute rounded-full pointer-events-none"
          style={{ top: -50, right: -50, width: 240, height: 240, background: 'radial-gradient(circle, rgba(240,184,12,0.18), transparent 70%)' }} />

        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full font-bold tracking-wider"
            style={{ padding: '5px 10px', background: 'rgba(240,184,12,0.12)', border: '1px solid rgba(240,184,12,0.3)', fontSize: 9, color: 'var(--yellow-2)' }}>
            <span className="inline-block rounded-full" style={{ width: 5, height: 5, background: 'var(--yellow)' }} />
            VOICE-FIRST AI · NEW
          </div>
          <h1 className="text-white mt-4" style={{ fontSize: 40, letterSpacing: '-0.03em', lineHeight: 0.95 }}>
            The world&apos;s first <span style={{ color: 'var(--yellow)' }}>voice-native</span> school.
          </h1>
          <p className="mt-3.5 leading-relaxed" style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)' }}>
            Skip the typing. Just talk. Lincoln teaches every subject — in 3 languages.
          </p>

          <div className="flex flex-col items-center gap-3.5" style={{ marginTop: 28 }}>
            <LincolnAvatar size={140} pulse />

            <div className="w-full flex flex-col items-center gap-3 rounded-3xl"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', padding: 18 }}>
              <div className="inline-flex items-center gap-1.5 rounded-full font-bold tracking-wider"
                style={{ padding: '4px 10px', background: 'rgba(34,181,114,0.18)', color: '#7be3b0', fontSize: 10 }}>
                <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: 'var(--green)' }} />
                TAP TO TALK
              </div>
              <button type="button" className="relative inline-flex items-center justify-center rounded-full"
                style={{ width: 88, height: 88, background: 'linear-gradient(135deg, var(--yellow), #d99a06)', boxShadow: '0 0 0 8px rgba(240,184,12,0.16), 0 12px 28px rgba(240,184,12,0.4)' }}>
                {[0, 1].map((i) => (
                  <span key={i} className="absolute rounded-full"
                    style={{ inset: -6, border: '2px solid rgba(240,184,12,0.5)', animation: `lc-pulse-ring 2s ${i * 0.7}s ease-out infinite` }} />
                ))}
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--navy)" strokeWidth="2.4">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                </svg>
              </button>
              <div className="italic" style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                &ldquo;Explain photosynthesis…&rdquo;
              </div>
              <Waveform bars={20} color="var(--yellow)" />
            </div>
          </div>

          <div className="flex flex-col gap-2.5 mt-5">
            <a href="#" className="lc-btn primary" style={{ justifyContent: 'center' }}>Talk to Lincoln now</a>
            <Link to="/providers" className="lc-btn ghost-light" style={{ justifyContent: 'center' }}>For schools &amp; providers →</Link>
          </div>
        </div>
      </section>

      {/* Trust marquee */}
      <section className="overflow-hidden" style={{ background: 'var(--navy-2)', color: 'rgba(255,255,255,0.7)', padding: '20px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="text-center font-bold uppercase tracking-widest mb-2.5" style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>TRUSTED BY</div>
        <div className="flex gap-6" style={{ animation: 'lc-marquee 20s linear infinite', width: 'max-content', padding: '0 20px' }}>
          {[...Array(2)].flatMap((_, dup) =>
            ['WEF', 'Malta MOE', 'Finland OPH', 'Fontana, CA', 'Glowie', 'Aalto Univ.'].map((t, i) => (
              <span key={`${dup}-${i}`} className="font-serif whitespace-nowrap" style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                {t} <span style={{ color: 'var(--yellow)', margin: '0 14px' }}>★</span>
              </span>
            ))
          )}
        </div>
      </section>

      {/* Two-audience */}
      <section className="bg-white" style={{ padding: '40px 20px' }}>
        <div className="text-center mb-6">
          <span className="lc-eyebrow">Two ways in</span>
          <h2 className="mt-2.5" style={{ fontSize: 30 }}>One school. Two doors.</h2>
        </div>
        <div className="flex flex-col gap-3.5">
          <div className="relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #fff8e1, #fff3c9)', border: '1px solid rgba(240,184,12,0.3)', borderRadius: 20, padding: 24 }}>
            <div className="absolute rounded-full" style={{ top: -40, right: -40, width: 140, height: 140, background: 'rgba(240,184,12,0.3)' }} />
            <div className="relative">
              <div className="inline-flex font-bold tracking-wider mb-3 rounded-full"
                style={{ padding: '4px 10px', background: 'rgba(240,184,12,0.25)', color: '#8a6b00', fontSize: 10 }}>
                🎓 FOR LEARNERS
              </div>
              <h3 style={{ fontSize: 24 }}>I want to learn.</h3>
              <p className="mt-2 mb-4" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                Voice tutor in 3 languages. Quizzes, flashcards, QR certificates. Free.
              </p>
              <Link to="/courses" className="lc-btn yellow sm w-full" style={{ justifyContent: 'center' }}>Start learning →</Link>
            </div>
          </div>
          <div className="relative overflow-hidden text-white"
            style={{ background: 'linear-gradient(135deg, var(--navy), var(--navy-3))', borderRadius: 20, padding: 24 }}>
            <div className="absolute rounded-full" style={{ top: -40, right: -40, width: 140, height: 140, background: 'rgba(34,181,114,0.25)' }} />
            <div className="relative">
              <div className="inline-flex font-bold tracking-wider mb-3 rounded-full"
                style={{ padding: '4px 10px', background: 'rgba(34,181,114,0.18)', color: '#7be3b0', fontSize: 10 }}>
                FOR PROVIDERS
              </div>
              <h3 className="text-white" style={{ fontSize: 24 }}>I want to teach.</h3>
              <p className="mt-2 mb-4" style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                Upload your materials. Lincoln becomes a tutor on them. Branded certs included.
              </p>
              <Link to="/providers" className="lc-btn primary sm w-full" style={{ justifyContent: 'center' }}>Become a provider →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features list */}
      <section className="bg-white" style={{ padding: '40px 20px' }}>
        <div className="text-center mb-6">
          <span className="lc-eyebrow">Inside Lincoln</span>
          <h2 className="mt-2.5" style={{ fontSize: 28 }}>10 ways to learn.</h2>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { icon: '🎙', title: 'Voice tutor',       color: 'rgba(240,184,12,0.15)', textColor: '#8a6b00' },
            { icon: '📝', title: 'Auto-quizzes',      color: 'rgba(34,181,114,0.15)', textColor: 'var(--green-2)' },
            { icon: '🏅', title: 'QR certificates',   color: 'rgba(43,108,217,0.15)', textColor: 'var(--blue)' },
            { icon: '🃏', title: 'Flashcards',        color: 'rgba(122,58,138,0.15)', textColor: '#7a3a8a' },
            { icon: '🌐', title: '3 languages',     color: 'rgba(201,120,100,0.15)', textColor: '#c97864' },
            { icon: '📞', title: 'Voice calling',     color: 'rgba(11,31,56,0.15)',   textColor: 'var(--navy)' },
            { icon: '📊', title: 'Progress tracker',  color: 'rgba(43,108,217,0.15)', textColor: 'var(--blue)' },
            { icon: '📷', title: 'Snap & solve',      color: 'rgba(240,184,12,0.15)', textColor: '#8a6b00' },
            { icon: '📺', title: 'Worksheets',        color: 'rgba(34,181,114,0.15)', textColor: 'var(--green-2)' },
            { icon: '💡', title: 'Suggested Qs',      color: 'rgba(122,58,138,0.15)', textColor: '#7a3a8a' },
          ].map((f) => (
            <div key={f.title} className="flex flex-col gap-2" style={{ padding: 16, borderRadius: 14, background: f.color }}>
              <span style={{ fontSize: 24 }}>{f.icon}</span>
              <span className="font-bold" style={{ fontSize: 13, color: f.textColor }}>{f.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Courses horizontal scroll */}
      <section style={{ background: 'var(--bg-soft)', padding: '40px 20px' }}>
        <div className="flex justify-between items-end mb-5">
          <div>
            <span className="lc-eyebrow">Featured</span>
            <h2 className="mt-1.5" style={{ fontSize: 24 }}>Top courses</h2>
          </div>
          <Link to="/courses" className="font-bold" style={{ fontSize: 12, color: 'var(--green-2)' }}>See all →</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto" style={{ padding: '0 0 12px', margin: '0 -20px', paddingLeft: 20, paddingRight: 20 }}>
          {COURSES.map((c) => (
            <Link key={c.slug} to={`/courses/${c.slug}`} className="bg-white overflow-hidden flex-shrink-0"
              style={{ minWidth: 220, borderRadius: 14, border: '1px solid var(--line)' }}>
              <div className="relative overflow-hidden" style={{ height: 100, background: c.color }}>
                <ImageSlot id={`mob-course-${c.slug}`} style={{ position: 'absolute', inset: 0 }} shape="rect" placeholder="" />
                <span className="absolute font-bold pointer-events-none"
                  style={{ top: 8, right: 8, background: 'var(--yellow)', color: 'var(--navy)', padding: '2px 6px', borderRadius: 3, fontSize: 10 }}>{c.icon}</span>
              </div>
              <div style={{ padding: 14 }}>
                <div className="font-bold uppercase tracking-wider" style={{ fontSize: 10, color: 'var(--muted)' }}>{c.provider}</div>
                <div className="font-bold mt-1" style={{ fontSize: 14 }}>{c.title}</div>
                <div className="mt-2" style={{ fontSize: 11, color: 'var(--muted)' }}>⏱ {c.duration} · 👥 {c.learners}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Case studies list */}
      <section className="bg-white" style={{ padding: '40px 20px' }}>
        <div className="text-center mb-6">
          <span className="lc-eyebrow">Case studies</span>
          <h2 className="mt-2.5" style={{ fontSize: 28 }}>Real classrooms.</h2>
        </div>
        <div className="flex flex-col gap-3.5">
          {CASE_STUDIES.slice(0, 4).map((cs) => (
            <Link key={cs.slug} to={`/case-studies/malta`} className="lc-card overflow-hidden flex" style={{ height: 110 }}>
              <div className="relative flex-shrink-0" style={{ width: 110, background: cs.color }}>
                <ImageSlot id={`mob-${cs.img}`} style={{ position: 'absolute', inset: 0 }} shape="rect" placeholder="" />
              </div>
              <div className="flex flex-col justify-between flex-1 min-w-0" style={{ padding: 14 }}>
                <div>
                  <div className="font-bold tracking-wider" style={{ fontSize: 9, color: cs.color }}>
                    {cs.tag.toUpperCase()} · {cs.region.toUpperCase()}
                  </div>
                  <div className="font-bold leading-tight mt-1" style={{ fontSize: 14 }}>{cs.title}</div>
                </div>
                <div className="self-start font-bold"
                  style={{ fontSize: 11, color: 'var(--yellow-2)', background: 'var(--navy)', padding: '3px 8px', borderRadius: 4 }}>
                  {cs.metric}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Stats + CTA */}
      <section className="on-navy text-white" style={{ background: 'var(--navy)', padding: '40px 20px' }}>
        <span className="lc-eyebrow on-dark">By the numbers</span>
        <h2 className="text-white mt-2.5" style={{ fontSize: 28 }}>A school the size of a country.</h2>
        <div className="grid mt-6" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <Stat value="10" label="Countries" />
          <Stat value="55k" label="Learners" color="#7be3b0" />
          <Stat value="3" label="Languages" />
          <Stat value="98%" label="Completion" color="#7be3b0" />
        </div>
        <div className="mt-9"
          style={{ padding: 24, borderRadius: 18, background: 'linear-gradient(135deg, rgba(240,184,12,0.15), rgba(34,181,114,0.1))', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h3 className="text-white" style={{ fontSize: 22 }}>Ready to talk?</h3>
          <p className="mt-1.5" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>No signup. Just tap the mic.</p>
          <a href="#" className="lc-btn primary mt-4 w-full" style={{ justifyContent: 'center' }}>🎙 Try Lincoln now</a>
        </div>
      </section>

      {/* Footer (compact) */}
      <footer style={{ background: 'var(--navy-2)', color: 'rgba(255,255,255,0.7)', padding: '32px 20px' }}>
        <Logo inverted size={28} />
        <p className="mt-3.5 leading-relaxed" style={{ fontSize: 12 }}>The world&apos;s first voice-native school. Built in Finland.</p>
        <div className="flex gap-2 mt-4">
          {['F', '𝕏', 'in', '▶', '📷'].map((s) => (
            <span key={s} className="inline-flex items-center justify-center rounded-full font-bold text-white"
              style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.08)', fontSize: 11 }}>{s}</span>
          ))}
        </div>
        <div className="text-center mt-7 pt-4" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          © 2026 Lincoln School
        </div>
      </footer>

      {/* Floating mic CTA */}
      <div className="sticky" style={{ bottom: 16, padding: '0 20px', pointerEvents: 'none', display: 'flex', justifyContent: 'flex-end', marginTop: -64 }}>
        <button type="button" className="relative inline-flex items-center justify-center rounded-full"
          style={{ width: 64, height: 64, background: 'linear-gradient(135deg, var(--yellow), #d99a06)', boxShadow: '0 12px 32px rgba(240,184,12,0.5)', pointerEvents: 'auto' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--navy)" strokeWidth="2.4">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          </svg>
          {[0, 1].map((i) => (
            <span key={i} className="absolute rounded-full"
              style={{ inset: -4, border: '2px solid rgba(240,184,12,0.5)', animation: `lc-pulse-ring 2s ${i * 0.7}s ease-out infinite` }} />
          ))}
        </button>
      </div>
    </div>
  );
}
